import { OnlineOrderListDTO } from '../dto/online-order-list-dto';
import { OrderDTO } from '../dto/order-dto';
import { ResourceInfoDTO } from '../dto/resource-info-dto';
import { ScenarioDTO } from '../dto/scenario-dto';
import { SubStateDTO } from '../dto/sub-state-dto';
import { LFOrderList } from '../models/lforder-list';
import { OrderData } from '../models/order-data';
import { OrderStateList } from '../models/order-state-list';
import { ResourceInfo } from '../models/resource-info';
import { Scenario } from '../models/scenario';
import { SubStateItem } from '../models/sub-state-item';

export class OrderFactory {
  static fromOnlineOrderDTOList(dtoList: OnlineOrderListDTO, projectId: string): LFOrderList {
    const list = new LFOrderList();
    list.data = dtoList?.items.map((dto) => this.fromOrderDTO(dto)) ?? [];
    list.projectId = projectId;
    return list;
  }

  static fromOrderDTO(dto: OrderDTO): OrderData {
    const item = new OrderData();
    item.id = dto.id;
    item.text = dto.text;
    item.orderIndex = dto.orderIndex;
    item.orderState = OrderStateList.getStateItem(dto.orderState);
    item.orderSubState =
      dto.orderSubState === undefined || dto.orderSubState === null
        ? undefined
        : this.fromSubStateDTO(dto.orderSubState);
    item.scenario =
      dto.scenario === undefined || dto.scenario === null ? undefined : this.fromScenarioDTO(dto.scenario);
    item.assetName = dto.assetName;
    item.assetPath = dto.assetPath;
    item.virtualLiveFormName = dto.liveFormName;
    item.completionDate = dto.completionDate;
    item.activeUserName = dto.activeUserName ?? '';
    item.lastEditUserName = dto.lastEditUserName ?? '';
    item.userSince = dto.userActiveSince ?? '';
    item.isEditAllowed = dto.isEditAllowed;
    item.hasReport = dto.hasReport;
    return item;
  }

  static fromSubStateDTO(dto: SubStateDTO): SubStateItem {
    const item = new SubStateItem();
    item.description = dto.description;
    item.text = dto.text;
    item.backgroundColor = dto.backgroundColor;
    item.foregroundColor = dto.foregroundColor;
    item.orderCanBeClosed = dto.orderCanBeClosed;
    item.originalValue = dto.originalValue;
    item.displayText = dto.text;
    return item;
  }

  static fromScenarioDTO(dto: ScenarioDTO): Scenario {
    const item = new Scenario();
    item.name = dto.name;
    item.color = dto.color;
    item.interval = dto.interval;
    item.unitText = dto.unitText;
    return item;
  }

  static toOrderDTO(data: OrderData): OrderDTO {
    return {
      id: data.id,
      text: data.text,
      orderIndex: data.orderIndex,
      completionDate: data.completionDate,
      orderState: data.orderState.state,
      assetName: data.assetName,
      assetPath: data.assetPath,
      liveFormName: data.virtualLiveFormName,
      orderSubState: data.orderSubState === undefined ? undefined : this.toSubStateDTO(data.orderSubState),
      scenario: data.scenario === undefined ? undefined : this.toScenarioDTO(data.scenario),
      activeUserName: '', // not relevant for backend
      userActiveSince: '', // not relevant for backend
      lastEditUserName: '', // not relevant for backend
      isEditAllowed: false, // not relevant for backend
      hasReport: false // not relevant for backend
    };
  }

  static toSubStateDTO(data: SubStateItem): SubStateDTO {
    return {
      orderCanBeClosed: data.orderCanBeClosed ?? false,
      description: data.description ?? '',
      text: data.text ?? '',
      backgroundColor: data.backgroundColor ?? '',
      foregroundColor: data.foregroundColor ?? '',
      originalValue: data.originalValue ?? ''
    };
  }

  static toScenarioDTO(data: Scenario): ScenarioDTO {
    return {
      name: data.name ?? '',
      color: data.color ?? '',
      interval: data.interval ?? 0,
      unitText: data.unitText ?? ''
    };
  }

  static fromResourceInfoDTO(data: ResourceInfoDTO): ResourceInfo {
    return {
      name: data.name,
      dataType: data.dataType,
      fileId: data.fileId ?? data.name + data.dataType // type like ".jpeg"
    };
  }
}
